import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import type { SnackbarContextType } from '@/contexts/SnackbarContext.types';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

type UserOrganization = components['schemas']['UserOrganization'];

export interface UseManageUserBudgetGroupsReturn {
  addUsers: (organizationId: number, selectedUserEmails: string[]) => Promise<UserOrganization[] | null>;
  removeUser: (organizationId: number, userEmail: string) => Promise<boolean>;
  loading: boolean;
  error: string | null;
  removeUserMutation: UseMutationResult<
    boolean,
    unknown,
    {
      organizationId: number;
      userEmail: string;
    },
    unknown
  >;
}

interface UseManageAccountUsersOrganizationConfig {
  snackbar: SnackbarContextType;
  organizationId: number;
}

export function useManageAccountUsersOrganization({
  snackbar,
  organizationId,
}: UseManageAccountUsersOrganizationConfig): UseManageUserBudgetGroupsReturn {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const addUsersMutation = useMutation({
    mutationFn: async ({
      organizationId,
      selectedUserEmails,
    }: {
      organizationId: number;
      selectedUserEmails: string[];
    }): Promise<UserOrganization[]> => {
      const client = createApiClient(getAccessToken);
      const requestBody = {
        selectedUserEmails,
      };
      const result = await client.POST('/api/organizations/{organizationId}/users', {
        params: { path: { organizationId } },
        body: requestBody,
      });
      return Array.isArray(result.data) ? (result.data as UserOrganization[]) : [];
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['userById', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['organization-account-users-relation', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['accountUsers'],
          exact: false,
        }),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const addUsers = async (organizationId: number, selectedUserEmails: string[]): Promise<UserOrganization[] | null> => {
    try {
      const result = await addUsersMutation.mutateAsync({ organizationId, selectedUserEmails });
      return result;
    } catch {
      return null;
    }
  };

  const removeUserMutation = useMutation({
    mutationFn: async ({ organizationId, userEmail }: { organizationId: number; userEmail: string }): Promise<boolean> => {
      const client = createApiClient(getAccessToken);
      const { response } = await client.DELETE('/api/organizations/{organizationId}/users', {
        params: {
          path: { organizationId },
          query: { UserEmail: userEmail },
        },
      });
      return response.status === 204 || response.ok;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['userById', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['organization-account-users-relation', organizationId],
          exact: false,
        }),
        queryClient.invalidateQueries({
          queryKey: ['accountUsers'],
          exact: false,
        }),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeUser = async (organizationId: number, userEmail: string): Promise<boolean> => {
    try {
      return await removeUserMutation.mutateAsync({ organizationId, userEmail });
    } catch {
      return false;
    }
  };

  const loading = addUsersMutation.isPending || removeUserMutation.isPending;

  const error =
    addUsersMutation.error instanceof Error
      ? addUsersMutation.error.message
      : removeUserMutation.error instanceof Error
        ? removeUserMutation.error.message
        : null;

  return {
    addUsers,
    removeUser,
    loading,
    error,
    removeUserMutation,
  };
}
